import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Message } from "primereact/message";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import toast from "react-hot-toast";
import {
  useDeleteSupportArticleMutation,
  useGetSupportArticlesQuery,
  useGetSupportCategoriesQuery,
} from "../../store/services/support";
import { SupportArticleDetailDialog } from "../../components/Dialogs/SupportArticleDetailDialog";
import {
  SupportArticleListItem,
  SupportArticlesQueryParams,
} from "../../models/support";
import { formatSupportDate } from "../../utils/functions/parseInstant";

const getApiError = (e: any): string => e?.data?.error?.message || "Ocurrió un error";

type StatusFilter = "all" | "active" | "inactive";
type FaqFilter = "all" | "faqs";

const statusOptions: { label: string; value: StatusFilter }[] = [
  { label: "Todos", value: "all" },
  { label: "Activos", value: "active" },
  { label: "Inactivos", value: "inactive" },
];

const faqOptions: { label: string; value: FaqFilter }[] = [
  { label: "Todos", value: "all" },
  { label: "Solo FAQs", value: "faqs" },
];

// Búsqueda tolerante a mayúsculas y acentos: "articulo" tiene que encontrar "Artículo".
const normalizeText = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

export function SupportArticles() {
  const navigate = useNavigate();
  const [titleSearch, setTitleSearch] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [faqFilter, setFaqFilter] = useState<FaqFilter>("all");
  const [articleToDelete, setArticleToDelete] = useState<SupportArticleListItem>();
  const [articleToView, setArticleToView] = useState<SupportArticleListItem>();

  const { data: categories, isLoading: isLoadingCategories } = useGetSupportCategoriesQuery();

  const queryParams: SupportArticlesQueryParams = useMemo(
    () => ({
      ...(categoryId ? { categoryId } : {}),
      ...(statusFilter === "active"
        ? { isActive: true }
        : statusFilter === "inactive"
        ? { isActive: false }
        : {}),
      ...(faqFilter === "faqs" ? { isFeatured: true } : {}),
    }),
    [categoryId, statusFilter, faqFilter]
  );

  const { data: articles, isLoading, isFetching } = useGetSupportArticlesQuery(queryParams);
  const [deleteArticle, { isLoading: isDeleting }] = useDeleteSupportArticleMutation();

  const categoryOptions = useMemo(
    () => (categories ?? []).map((c) => ({ label: c.name, value: c.id })),
    [categories]
  );

  const filteredArticles = useMemo(() => {
    const term = normalizeText(titleSearch);
    if (!term) return articles ?? [];
    return (articles ?? []).filter((article) => normalizeText(article.title).includes(term));
  }, [articles, titleSearch]);

  const handleDelete = () => {
    if (!articleToDelete) return;
    deleteArticle(articleToDelete.id)
      .unwrap()
      .then(() => {
        toast.success("Artículo eliminado exitosamente!");
      })
      .catch((error) => {
        toast.error(getApiError(error));
      })
      .finally(() => setArticleToDelete(undefined));
  };

  const tagsBodyTemplate = (row: SupportArticleListItem) => (
    <div className="flex flex-wrap gap-1">
      {row.searchTags.length > 0
        ? row.searchTags.map((tag) => <Tag key={tag} value={tag} severity="info" />)
        : "—"}
    </div>
  );

  const faqBodyTemplate = (row: SupportArticleListItem) =>
    row.isFeatured ? <Tag severity="success" value="FAQ" /> : <span className="text-500">—</span>;

  const activeBodyTemplate = (row: SupportArticleListItem) =>
    row.isActive ? (
      <Tag severity="success" value="Activo" />
    ) : (
      <Tag severity="danger" value="Inactivo" />
    );

  const feedbackBodyTemplate = (row: SupportArticleListItem) => {
    const total = row.feedbackYes + row.feedbackNo;
    const needsReview = total > 0 && row.feedbackNo > total * 0.3;
    return (
      <div className="flex align-items-center gap-2">
        <span>{row.feedbackPct === null ? "—" : `${row.feedbackPct}%`}</span>
        {needsReview && <Tag severity="danger" value="Requiere revisión" />}
      </div>
    );
  };

  const updatedBodyTemplate = (row: SupportArticleListItem) =>
    formatSupportDate(row.updatedAt);

  const actionsBodyTemplate = (row: SupportArticleListItem) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-eye"
        rounded
        outlined
        tooltip="Ver detalle"
        tooltipOptions={{ position: "top" }}
        onClick={() => setArticleToView(row)}
      />
      <Button
        icon="pi pi-pencil"
        rounded
        outlined
        severity="info"
        tooltip="Editar"
        tooltipOptions={{ position: "top" }}
        onClick={() => navigate(`/soporte/articulos/${row.id}`)}
      />
      <Button
        icon="pi pi-trash"
        rounded
        outlined
        severity="danger"
        tooltip="Eliminar"
        tooltipOptions={{ position: "top" }}
        onClick={() => setArticleToDelete(row)}
      />
    </div>
  );

  const deleteDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        severity="secondary"
        onClick={() => setArticleToDelete(undefined)}
        disabled={isDeleting}
      />
      <Button
        label="Eliminar"
        icon="pi pi-trash"
        severity="danger"
        onClick={handleDelete}
        loading={isDeleting}
      />
    </div>
  );

  return (
    <>
      <div className="p-card gap-4 p-3">
        <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
          <h1 className="text-center m-0">Artículos de soporte</h1>
        </div>

        <div className="flex flex-wrap align-items-end justify-content-between gap-3 mb-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex flex-column gap-2">
              <label htmlFor="titleSearch" className="text-600 text-sm">
                Buscar
              </label>
              <IconField iconPosition="left" className="w-full">
                <InputIcon className="pi pi-search" />
                <InputText
                  id="titleSearch"
                  value={titleSearch}
                  onChange={(e) => setTitleSearch(e.target.value)}
                  placeholder="Buscar por título"
                  className="w-full"
                  style={{ minWidth: "14rem" }}
                />
              </IconField>
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="categoryFilter" className="text-600 text-sm">
                Categoría
              </label>
              <Dropdown
                id="categoryFilter"
                value={categoryId}
                options={categoryOptions}
                optionLabel="label"
                optionValue="value"
                loading={isLoadingCategories}
                onChange={(e) => setCategoryId(e.value ?? null)}
                placeholder="Todas las categorías"
                showClear
                style={{ minWidth: "16rem" }}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="statusFilter" className="text-600 text-sm">
                Estado
              </label>
              <Dropdown
                id="statusFilter"
                value={statusFilter}
                options={statusOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setStatusFilter(e.value)}
                style={{ minWidth: "10rem" }}
              />
            </div>
            <div className="flex flex-column gap-2">
              <label htmlFor="faqFilter" className="text-600 text-sm">
                FAQ
              </label>
              <Dropdown
                id="faqFilter"
                value={faqFilter}
                options={faqOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setFaqFilter(e.value)}
                style={{ minWidth: "10rem" }}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              label="Crear categoría"
              icon="pi pi-folder"
              severity="secondary"
              outlined
              onClick={() => navigate("/soporte/categorias")}
            />
            <Button
              label="Nuevo artículo"
              icon="pi pi-plus"
              onClick={() => navigate("/soporte/articulos/nuevo")}
              disabled={!isLoadingCategories && (categories ?? []).length === 0}
              tooltip={
                !isLoadingCategories && (categories ?? []).length === 0
                  ? "Primero creá una categoría para poder asignar el artículo"
                  : undefined
              }
              tooltipOptions={{ position: "bottom", showOnDisabled: true }}
            />
          </div>
        </div>

        {!isLoadingCategories && (categories ?? []).length === 0 && (
          <Message
            severity="warn"
            className="w-full mb-3"
            text="Todavía no hay categorías en este tenant. Creá al menos una para poder cargar artículos."
          />
        )}

        <DataTable
          value={filteredArticles}
          loading={isLoading || isFetching || isDeleting}
          tableStyle={{ minWidth: "70rem" }}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          responsiveLayout="scroll"
          emptyMessage={
            titleSearch.trim()
              ? "No hay artículos que coincidan con la búsqueda."
              : "No hay artículos para mostrar."
          }
        >
          <Column field="title" header="Título" style={{ minWidth: "16rem" }} />
          <Column
            field="categoryName"
            header="Categoría"
            body={(row: SupportArticleListItem) => row.categoryName || "—"}
            style={{ width: "12rem" }}
          />
          <Column header="Tags" body={tagsBodyTemplate} style={{ minWidth: "12rem" }} />
          <Column header="FAQ" body={faqBodyTemplate} style={{ width: "6rem" }} />
          <Column header="Activo" body={activeBodyTemplate} style={{ width: "8rem" }} />
          <Column field="version" header="Versión" style={{ width: "6rem" }} />
          <Column header="% Útil" body={feedbackBodyTemplate} style={{ width: "12rem" }} />
          <Column header="Actualizado" body={updatedBodyTemplate} style={{ width: "8rem" }} />
          <Column header="Acciones" body={actionsBodyTemplate} style={{ width: "11rem" }} />
        </DataTable>
      </div>

      <SupportArticleDetailDialog
        article={articleToView}
        onHide={() => setArticleToView(undefined)}
      />

      <Dialog
        visible={!!articleToDelete}
        header="Eliminar artículo"
        modal
        style={{ width: "28rem" }}
        footer={deleteDialogFooter}
        onHide={() => setArticleToDelete(undefined)}
      >
        <div className="flex align-items-center gap-3">
          <i className="pi pi-exclamation-triangle text-3xl text-orange-500"></i>
          <span>
            ¿Estás seguro de que querés eliminar el artículo{" "}
            <strong>{articleToDelete?.title}</strong>? Esta acción no se puede deshacer.
          </span>
        </div>
      </Dialog>
    </>
  );
}
